import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
  isFcmSupported,
  requestFcmToken,
  requestPermission,
  onForegroundMessage,
} from '../lib/firebase';
import {
  useRegisterTokenMutation,
  useUnregisterTokenMutation,
} from '../store/notificationsApi';
import { playNotificationSound } from '../lib/notificationSound';

// Local-storage key for the most recently registered token. Lets us:
//   • Skip re-registering the same token on every reload
//   • Clean up the right token on logout
const STORED_TOKEN_KEY = 'trustlab_fcm_token';

// Short audio cue for foreground notifications — synthesised live via the
// Web Audio API so no mp3 file is needed. To swap the chime style, edit
// DEFAULT_SOUND in src/lib/notificationSound.js.

// Brand the title — must mirror service-worker logic so foreground +
// background notifications look identical.
const brandTitle = (raw) => {
  const t = (raw || '').trim();
  if (!t) return 'TrustLab Diagnostics';
  if (/trustlab/i.test(t)) return t;
  return `TrustLab · ${t}`;
};

// Show a native OS notification while the page is open.
//
// We route through the ACTIVE service worker's showNotification() — the
// reverse of what you'd expect. Two reasons:
//   1. When a SW is registered, Chrome actually dispatches `new Notification()`
//      through the SW anyway, and sometimes drops it silently on the way.
//      Going through the SW directly skips that fragile hop.
//   2. reg.showNotification() supports `image`, `vibrate`, `actions` etc.
//      that `new Notification()` doesn't on every browser.
//
// Falls back to new Notification() only if no SW is controlling the page
// (dev without SW registration, Safari, etc.).
const showForegroundNotification = async (msg) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = brandTitle(msg.title);
  const options = {
    body: msg.body || '',
    icon: '/favicon.png',
    badge: '/favicon.png',
    tag: msg.type || 'trustlab-notification',
    renotify: true,
    vibrate: [120, 60, 120],
    data: { url: msg.url || '/' },
  };
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
      const reg = await navigator.serviceWorker.ready;
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(title, options);
        console.info('[fcm] foreground notification shown via SW');
        return;
      }
    }
    // Fallback for browsers without a controlling SW.
    const notif = new Notification(title, options);
    notif.onclick = (e) => {
      e.preventDefault();
      window.focus();
      if (msg.url) window.location.assign(msg.url);
      notif.close();
    };
    console.info('[fcm] foreground notification shown via Notification()');
  } catch (err) {
    console.warn('[fcm] showForegroundNotification failed:', err?.message || err);
  }
};

// Drives the whole web-push lifecycle for the customer site.
//
// Behaviour:
//   • Does nothing for guests.
//   • Once a user signs in, attempts to silently reuse an existing
//     `Notification.permission === 'granted'` and register the token.
//   • Exposes `enable()` for an explicit user-triggered prompt
//     (called from a "Turn on notifications" button).
//   • Listens for foreground messages and shows them as a toast +
//     fires an event so the bell badge can refresh.
//   • Tears down the token on sign-out.
export const usePushNotifications = () => {
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useUI();
  const [registerToken] = useRegisterTokenMutation();
  const [unregisterToken] = useUnregisterTokenMutation();

  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [supported, setSupported] = useState(true);
  const [registering, setRegistering] = useState(false);

  // Refs so the foreground listener captures the latest functions.
  const lastUserId = useRef(null);

  // Detect support on mount.
  useEffect(() => {
    isFcmSupported().then((ok) => setSupported(ok));
  }, []);

  // Foreground message handler — fires while the page is open. Shows a
  // toast + dispatches a custom event the bell can listen for to refetch.
  useEffect(() => {
    if (!supported) return undefined;
    const off = onForegroundMessage((msg) => {
      playNotificationSound();
      showForegroundNotification(msg);
      showToast?.(`${msg.title}${msg.body ? ' — ' + msg.body : ''}`);
      window.dispatchEvent(
        new CustomEvent('trustlab:notification', { detail: msg })
      );
    });
    return () => off?.();
  }, [supported, showToast]);

  // Internal: get a fresh token + send it to the backend.
  const persistToken = useCallback(async () => {
    setRegistering(true);
    try {
      const token = await requestFcmToken();
      if (!token) return null;
      // Skip the round trip if the same token is already registered.
      const cached = window.localStorage.getItem(STORED_TOKEN_KEY);
      if (cached === token) return token;
      await registerToken({
        token,
        platform: 'web',
        user_agent: navigator.userAgent || '',
      }).unwrap();
      window.localStorage.setItem(STORED_TOKEN_KEY, token);
      return token;
    } catch (err) {
      console.warn('[fcm] register failed:', err?.data?.message || err.message || err);
      return null;
    } finally {
      setRegistering(false);
    }
  }, [registerToken]);

  // Explicit opt-in — call from a button. Returns true if successfully enabled.
  const enable = useCallback(async () => {
    const result = await requestPermission();
    setPermission(result);
    if (result !== 'granted') return false;
    const token = await persistToken();
    return !!token;
  }, [persistToken]);

  // Auto-flow once a user signs in:
  //   • permission already granted   → silently register their FCM token
  //   • permission still 'default'   → after a 1.5s delay, prompt the browser
  //     (delay gives the page a chance to render first; Chrome is more
  //      forgiving when the prompt isn't tied to first paint)
  //   • permission was 'denied'      → log a hint, do nothing further
  useEffect(() => {
    if (!isAuthenticated || !supported) return undefined;
    if (typeof Notification === 'undefined') return undefined;

    const uid = user?._id || user?.id || 'unknown';
    if (lastUserId.current === uid) return undefined;
    lastUserId.current = uid;

    if (Notification.permission === 'granted') {
      console.info('[fcm] permission already granted — registering token');
      persistToken();
      return undefined;
    }
    if (Notification.permission === 'denied') {
      console.info('[fcm] notification permission previously denied — re-enable from browser settings');
      return undefined;
    }

    // permission === 'default' → ask once, after a short delay.
    const timer = setTimeout(async () => {
      console.info('[fcm] requesting notification permission…');
      const result = await requestPermission();
      console.info('[fcm] permission result:', result);
      setPermission(result);
      if (result === 'granted') {
        const token = await persistToken();
        console.info('[fcm] token registered:', token ? `${token.slice(0, 18)}…` : 'null');
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user, supported, persistToken]);

  // On sign-out: drop the token from the backend so this device stops
  // receiving pushes intended for the previous user.
  useEffect(() => {
    if (isAuthenticated) return;
    const cached = window.localStorage.getItem(STORED_TOKEN_KEY);
    if (!cached) return;
    unregisterToken({ token: cached }).catch(() => {});
    window.localStorage.removeItem(STORED_TOKEN_KEY);
    lastUserId.current = null;
  }, [isAuthenticated, unregisterToken]);

  return { permission, supported, registering, enable };
};

export default usePushNotifications;
